import { getDefaultOptions } from "../../services/Category";
import { simpleQuery } from "../../services/utils";

export interface MLMetrics {
  model_name: string;
  trained_at: string;
  n_train: number;
  n_test: number;
  accuracy: number;
  precision: number;
  recall: number;
  f1: number;
  auc_roc: number | null; // null when the test set has a single class
  features: string[];
  balanced_accuracy: number;
  mcc: number;
  is_active: boolean;
  training_status: "pending" | "running" | "done" | "error";
  training_start_date: string | null;
  training_end_date: string | null;
  feature_importances: Record<string, number>;
  confusion_matrix: [number, number, number, number] | []; // [TN, FP, FN, TP]
  roc_curve: { fpr: number[]; tpr: number[] } | Record<string, never>;
}

export interface AvailableFeatures {
  quanti: string[];
  quali: string[];
}

export const fetchAvailableFeatures = async (): Promise<AvailableFeatures> => {
  return simpleQuery("/api/ml-analysis/available-features/");
};

export interface FeatureTestResult {
  variable: string;
  p_value: number;
  score: number;
  score_label: string;
  significant: boolean;
}

export interface FeatureTestResponse {
  quanti: FeatureTestResult[];
  quali: FeatureTestResult[];
}

export const testFeatures = async (params: {
  quanti_features: string[];
  quali_features: string[];
}): Promise<FeatureTestResponse> => {
  const response = await fetch(
    "/api/ml-analysis/test-features/",
    getDefaultOptions({ method: "POST", body: JSON.stringify(params) })
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error ?? `Erreur ${response.status}`);
  }
  return data as FeatureTestResponse;
};

export const trainCustomModel = async (params: {
  quanti_features: string[];
  quali_features: string[];
}): Promise<void> => {
  const response = await fetch(
    "/api/ml-analysis/train-custom/",
    getDefaultOptions({ method: "POST", body: JSON.stringify(params) })
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (data as { error?: string })?.error ?? `Erreur ${response.status}`
    );
  }
};

export const fetchMLMetrics = async (): Promise<MLMetrics[]> => {
  return simpleQuery("/api/ml-analysis/metrics/");
};

export interface FeatureImportanceItem {
  variable: string;
  test: string;
  p_value: number | null;
  score: number | null;
  score_label: string;
}

export interface FeatureImportanceResponse {
  quanti: FeatureImportanceItem[];
  quali: FeatureImportanceItem[];
}

export const fetchFeatureImportance =
  async (): Promise<FeatureImportanceResponse> => {
    return simpleQuery("/api/ml-analysis/feature-importance/");
  };

export const resetCustomModel = async (): Promise<void> => {
  const response = await fetch(
    "/api/ml-analysis/reset-custom/",
    getDefaultOptions({ method: "POST", body: JSON.stringify({}) })
  );
  if (!response.ok) throw new Error(`Erreur ${response.status}`);
};

export interface BreakdownRow {
  modality: string;
  actual_sales: number;
  materialized_sales: number;
  ml_cancellations: number;
  cancelled_sales: number;
}

export type PredictionBreakdown = Record<string, BreakdownRow[]>;

export const fetchPredictionBreakdown = async (
  year?: number
): Promise<PredictionBreakdown> => {
  const params = year ? `?year=${year}` : "";
  return simpleQuery(`/api/ml-analysis/prediction-breakdown/${params}`);
};

export const setActiveModel = async (model_name: string): Promise<void> => {
  const response = await fetch(
    "/api/ml-analysis/set-active-model/",
    getDefaultOptions({ method: "POST", body: JSON.stringify({ model_name }) })
  );
  if (!response.ok) throw new Error(`Erreur ${response.status}`);
};
